import 'es-iterator-helpers/auto'

import { datadogRum } from '@datadog/browser-rum'
import { ErrorBoundary, reactPlugin } from '@datadog/browser-rum-react'
import Tenant from '@modules/tenant/Tenant'
import { OpenFeatureProvider } from '@openfeature/react-sdk'
import { inject } from '@vercel/analytics'
import { Analytics } from '@vercel/analytics/react'
import Gleap from 'gleap'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import {
  DevelopmentErrorFallback,
  ProductionErrorFallback,
} from './components/ErrorFallback'
import './index.css'
import reportWebVitals from './reportWebVitals'
import AppRoutes from './routes/AppRoutes'
import { store } from './store/store'
import { DATADOG } from './utils/datadog'
import { initFeatureFlags, isDev, isProduction } from './utils/featureFlags'

if (!isDev) {
  datadogRum.init({
    applicationId: DATADOG.applicationId,
    clientToken: DATADOG.clientToken,
    site: DATADOG.site,
    service: DATADOG.service,
    env: import.meta.env.VITE_ENVIRONMENT,
    version: __APP_VERSION__,
    sessionSampleRate: 100,
    traceSampleRate: 100,
    sessionReplaySampleRate: 50,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin()],
  })
  datadogRum.startSessionReplayRecording()
}

// After RUM, so each session records the flags its admin had
initFeatureFlags()

// Enable Vercel analytics
inject({
  mode: isProduction ? 'production' : 'development',
})

//Gleap init
if (import.meta.env.VITE_GLEAP_PROJECT_ID) {
  Gleap.initialize(import.meta.env.VITE_GLEAP_PROJECT_ID)
  Gleap.setEnvironment(isProduction ? 'prod' : 'staging')
  Gleap.hideAiChatbar()
}

const root = createRoot(document.getElementById('root') as HTMLElement)

root.render(
  <StrictMode>
    <BrowserRouter basename="/">
      <ErrorBoundary
        fallback={
          isProduction ? ProductionErrorFallback : DevelopmentErrorFallback
        }
      >
        <OpenFeatureProvider>
          <Provider store={store}>
            <Tenant>
              <AppRoutes />
            </Tenant>
          </Provider>
        </OpenFeatureProvider>
        <Analytics />
      </ErrorBoundary>
    </BrowserRouter>
  </StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
